import * as React from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router";
import { Shell } from "./layouts/Shell";
import { ADMIN_NAV, APP_NAV, MASTER_NAV } from "./layouts/nav";
import { useSession } from "./lib/session";
import { useBrand } from "./lib/brand";

const L = (f: () => Promise<{ default: React.ComponentType<any> }>) => {
  const C = React.lazy(f);
  return (
    <React.Suspense fallback={<div className="p-6 text-sm text-muted">Carregando…</div>}>
      <C />
    </React.Suspense>
  );
};

function FullPageLoading() {
  return <div className="flex h-full items-center justify-center text-sm text-muted">Carregando…</div>;
}

/** Guards de UI complementam — nunca substituem — a autorização no servidor. */
function RequireUser() {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <FullPageLoading />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <Outlet />;
}

function RequireWorkspace() {
  const { me } = useSession();
  if (me?.kind === "user" && !me.active_workspace_id) {
    if (me.is_brand_admin && me.workspaces.length === 0) return <Navigate to="/admin/overview" replace />;
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

function RequireBrandAdmin() {
  const { me } = useSession();
  if (!me?.is_brand_admin) return <Navigate to="/app/overview" replace />;
  return <Outlet />;
}

function RequirePlatform() {
  const { me, loading } = useSession();
  if (loading) return <FullPageLoading />;
  if (!me) return <Navigate to="/master/login" replace />;
  return <Outlet />;
}

function Home() {
  const brand = useBrand();
  if (brand.kind === "platform") return <Navigate to="/master/overview" replace />;
  return L(() => import("./pages/public/Landing"));
}

export function buildRouter(platform: boolean) {
  if (platform) {
    return createBrowserRouter([
      { path: "/master/login", element: L(() => import("./pages/auth/MasterLogin")) },
      {
        element: <RequirePlatform />,
        children: [
          {
            path: "/master",
            element: <Shell groups={MASTER_NAV} panel="master" />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: "overview", element: L(() => import("./pages/master/Overview")) },
              { path: "brands", element: L(() => import("./pages/master/Brands")) },
              { path: "billing", element: L(() => import("./pages/master/Billing")) },
              { path: "whatsapp", element: L(() => import("./pages/master/WhatsappQr")) },
              { path: "meta-webhook", element: L(() => import("./pages/master/MetaWebhook")) },
              { path: "operations", element: L(() => import("./pages/master/Operations")) },
              { path: "audit", element: L(() => import("./pages/master/Audit")) },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/master/overview" replace /> },
    ]);
  }
  return createBrowserRouter([
    { path: "/", element: <Home /> },
    // Planos ficam na própria landing (seção #planos).
    { path: "/pricing", element: <Navigate to="/" replace /> },
    { path: "/share/:token", element: L(() => import("./pages/public/SharedReport")) },
    { path: "/login", element: L(() => import("./pages/auth/Login")) },
    { path: "/signup", element: L(() => import("./pages/auth/Signup")) },
    { path: "/forgot-password", element: L(() => import("./pages/auth/ForgotPassword")) },
    { path: "/reset-password", element: L(() => import("./pages/auth/ResetPassword")) },
    { path: "/verify-email", element: L(() => import("./pages/auth/VerifyEmail")) },
    { path: "/invite/:token", element: L(() => import("./pages/auth/Invite")) },
    {
      element: <RequireUser />,
      children: [
        { path: "/onboarding", element: L(() => import("./pages/app/onboarding/Onboarding")) },
        {
          element: <RequireWorkspace />,
          children: [
            {
              path: "/app",
              element: <Shell groups={APP_NAV} panel="app" />,
              children: [
                { index: true, element: <Navigate to="overview" replace /> },
                { path: "overview", element: L(() => import("./pages/app/overview/Overview")) },
                { path: "acquisition", element: L(() => import("./pages/app/acquisition/Acquisition")) },
                { path: "creatives", element: L(() => import("./pages/app/creatives/Creatives")) },
                { path: "attribution", element: L(() => import("./pages/app/attribution/Attribution")) },
                { path: "planning", element: L(() => import("./pages/app/planning/Planning")) },
                // A análise é por link (tela do link); o endereço da análise geral leva à lista de links.
                { path: "link-analytics", element: <Navigate to="/app/tracking/links" replace /> },
                { path: "leads", element: L(() => import("./pages/app/leads/Leads")) },
                { path: "leads/:id", element: L(() => import("./pages/app/leads/LeadDetail")) },
                // layout "fill": página ocupa toda a largura e a altura restante da tela (kanban com rolagem própria).
                { path: "opportunities", element: L(() => import("./pages/app/opportunities/Opportunities")), handle: { layout: "fill" } },
                { path: "revenue", element: L(() => import("./pages/app/revenue/Revenue")) },
                { path: "organization", element: L(() => import("./pages/app/organization/OrganizationOverview")) },
                { path: "pipelines", element: L(() => import("./pages/app/pipelines/Pipelines")) },
                // Canais agora são geridos dentro de cada integração; o endereço antigo leva para lá.
                { path: "channels", element: <Navigate to="/app/integrations" replace /> },
                { path: "integrations", element: L(() => import("./pages/app/integrations/Integrations")) },
                { path: "integrations/meta", element: L(() => import("./pages/app/integrations/meta/MetaConnect")) },
                { path: "integrations/:id", element: L(() => import("./pages/app/integrations/IntegrationDetail")) },
                { path: "forms", element: L(() => import("./pages/app/forms/Forms")) },
                { path: "automations", element: L(() => import("./pages/app/automations/Automations")) },
                { path: "automations/:id", element: L(() => import("./pages/app/automations/AutomationDetail")) },
                { path: "audiences", element: L(() => import("./pages/app/audiences/Audiences")) },
                { path: "tracking/sites", element: L(() => import("./pages/app/tracking/Sites")) },
                { path: "tracking/sites/:id", element: L(() => import("./pages/app/tracking/SiteDetail")) },
                { path: "tracking/links", element: L(() => import("./pages/app/tracking/Links")) },
                { path: "tracking/links/:id", element: L(() => import("./pages/app/tracking/LinkDetail")) },
                { path: "tracking/events", element: L(() => import("./pages/app/tracking/Events")) },
                { path: "reports", element: L(() => import("./pages/app/reports/Reports")) },
                { path: "developers", element: L(() => import("./pages/app/developers/Developers")) },
                { path: "settings", element: L(() => import("./pages/app/settings/Settings")) },
                { path: "settings/workspaces", element: L(() => import("./pages/app/settings/Workspaces")) },
                { path: "settings/custom-fields", element: L(() => import("./pages/app/settings/CustomFields")) },
                { path: "settings/support", element: L(() => import("./pages/app/settings/SupportGrants")) },
                { path: "team", element: L(() => import("./pages/app/team/Team")) },
                { path: "billing", element: L(() => import("./pages/app/billing/Billing")) },
              ],
            },
          ],
        },
        {
          element: <RequireBrandAdmin />,
          children: [
            {
              path: "/admin",
              element: <Shell groups={ADMIN_NAV} panel="admin" />,
              children: [
                { index: true, element: <Navigate to="overview" replace /> },
                { path: "overview", element: L(() => import("./pages/admin/Overview")) },
                { path: "organizations", element: L(() => import("./pages/admin/Organizations")) },
                { path: "plans", element: L(() => import("./pages/admin/Plans")) },
                { path: "billing", element: L(() => import("./pages/admin/Billing")) },
                { path: "branding", element: L(() => import("./pages/admin/Branding")) },
                { path: "email", element: L(() => import("./pages/admin/Email")) },
                { path: "team", element: L(() => import("./pages/admin/Team")) },
              ],
            },
          ],
        },
      ],
    },
    { path: "*", element: L(() => import("./pages/public/NotFound")) },
  ]);
}
